import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";

// Context
import { usePulseSurveys } from "./Data/LandingPageContext";
import { useSelector } from "react-redux";

// Local Components
import SurveyCard from "./components/SurveyCard";
import SurveyDetailSheet from "./components/SurveyDetailSheet";

// Note: EmailTemplate and AssignSection imports removed - now used in PulseScheduleSurvey component

// Libraries
import culture_factor_pulses from "./lib/culture_factor_pulses.json";

// Styles
import colors from "styles/colors";

// Icons
const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
  </svg>
);
// Note: QuickSend modal styled-components removed - now using page-based flow

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const staggeredFadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageContainer = styled.div`
  padding: 32px;
  background-color: ${colors.background.white};
  min-height: 100vh;
  animation: ${fadeInUp} 0.5s ease-out;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const MainTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  color: ${colors.text.dark};
  margin: 0;
  line-height: 1.2;
`;

const Subtitle = styled.p`
  font-size: 16px;
  font-weight: 400;
  color: ${colors.text.gray};
  margin: 0;
  line-height: 1.4;
`;

const BackLink = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover {
    background: ${props => props.disabled ? 'transparent' : '#f8f9fa'};
    border-color: ${props => props.disabled ? '#e0e0e0' : '#476dfa'};
    color: ${props => props.disabled ? '#7e7e7e' : '#476dfa'};
  }
`;

const CreateButton = styled.button`
  background-color: ${colors.primary[500]};
  color: ${colors.background.white};
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${colors.primary[600]};
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const ContentContainer = styled.div`
  display: flex;
  gap: 32px;
`;

const Sidebar = styled.div`
  width: 280px;
  min-width: 280px;
`;

const MainContent = styled.div`
  flex: 1;
`;

const LibrarySection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.gray};
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const LibraryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 4px;
  background-color: ${props => props.active ? colors.primary[50] : 'transparent'};
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${colors.primary[50]};
  }
`;

const LibraryItemText = styled.span`
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? colors.primary[600] : colors.text.gray};
  font-size: 14px;
`;

const CountBadge = styled.span`
  background-color: ${colors.neutral[200]};
  color: ${colors.text.gray};
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;

const SectionDivider = styled.hr`
  border: none;
  height: 1px;
  background-color: ${colors.neutral[200]};
  margin: 12px 0;
`;

const SearchContainer = styled.div`
  margin-bottom: 24px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  font-size: 14px;
  background-color: ${colors.background.lightBlue};
  color: ${colors.text.dark};
  outline: none;
  transition: border-color 0.2s ease;
  
  &:focus {
    border-color: ${colors.primary[500]};
  }
  
  &::placeholder {
    color: ${colors.text.gray};
  }
`;

const SurveyInfo = styled.div`
  margin-bottom: 24px;
`;

const SectionHeading = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.dark};
  margin: 0 0 4px 0;
  line-height: 1.3;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: ${colors.text.gray};
  margin: 0 0 8px 0;
  line-height: 1.5;
`;

const SurveyCount = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: ${colors.text.gray};
  margin: 0;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const SurveysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 24px;
  
  > * {
    animation: ${staggeredFadeIn} 0.6s ease-out;
    animation-fill-mode: both;
    
    ${Array.from({ length: 20 }, (_, i) => `
      &:nth-child(${i + 1}) {
        animation-delay: ${i * 0.1}s;
      }
    `).join('')}
  }
`;

const SurveyLibrary = ({ navigateToForm }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromSchedule = Boolean(location?.state?.fromSchedule);
  const returnTo = location?.state?.returnTo || "/leader/audit/schedule-v2";
  const handleBackToSurveys = () => {
    if (fromSchedule) {
      navigate(returnTo);
    } else {
      navigate(-1);
    }
  };
  const [searchValue, setSearchValue] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false);
  const [highlightedSurvey, setHighlightedSurvey] = useState(null);

  // Get current user info for permission checks
  const { get_auth } = useSelector((state) => ({ get_auth: state.auth }));
  const currentUserId = Number(get_auth?.employee?.id);

  // Handle navigation state for pre-selecting tab and highlighting survey
  useEffect(() => {
    if (location.state?.selectedType) {
      setSelectedType(location.state.selectedType);
    }
    if (location.state?.highlightSurveyName) {
      setHighlightedSurvey(location.state.highlightSurveyName);
      // Clear highlight after 3 seconds
      const timer = setTimeout(() => {
        setHighlightedSurvey(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [location.state]);


  // Helper function to get question type labels
  const getQuestionTypeLabel = (questionType) => {
    const typeLabels = {
      text: "Open Text",
      scale: "Sliding Scale",
      multi: "Multiple Answer",
      single: "Multiple Choice",
    };
    return typeLabels[questionType] || "Text";
  };

  // Get pulse templates from context (these are the drafts/templates)
  const { templatesData, rawTemplatesData } = usePulseSurveys();

  // Transform pulse templates data to match the expected format
  const pulseSurveys =
    templatesData?.map((template) => {
      // Find the original template data to get user_tasks
      const originalTemplate = rawTemplatesData?.results?.find(
        (t) => t.id === template.id
      );

      // Extract creator information
      const creatorTask = originalTemplate?.user_tasks?.find(
        (task) => task.task_type === "created_by"
      );

      const creatorName = creatorTask
        ? `${creatorTask.name}`
        : "Dr. Danielle Prowse";

      // Extract description from user_tasks
      const descriptionTask = originalTemplate?.user_tasks?.find(
        (task) => task.task_type === "description"
      );
      const description =
        descriptionTask?.text ||
        originalTemplate?.description ||
        `Template created: ${template.updated?.props?.children || "N/A"}`;

      // Extract questions from user_tasks
      const questionTasks =
        originalTemplate?.user_tasks?.filter(
          (task) => task.task_type === "question"
        ) || [];
      const questions = questionTasks.map((questionTask, index) => ({
        id: `${template.id}-q-${index}`, // Generate unique ID for each question
        question: questionTask.question,
        type: {
          value: questionTask.question_type || "text",
          label: getQuestionTypeLabel(questionTask.question_type || "text"),
        },
        aiEnhanced: questionTask.aiEnhanced || false,
        scale: questionTask.scale || null,
        qualifier: questionTask.qualifier || null,
        options: questionTask.options || null,
      }));

      return {
        id: template.id,
        title: template.name,
        type: "Pulse",
        description: description,
        questions: questions,
        creator: creatorName,
        originalTemplate: originalTemplate, // Pass original template for permission checks
      };
    }) || [];

  // Get all surveys from libraries and combine with pulse surveys
  const allSurveys = [...culture_factor_pulses.surveys, ...pulseSurveys];

  // Get unique survey types
  const surveyTypes = [
    "Retention",
    "Culture",
    "Leadership",
    "Onboarding",
    "Pulse",
  ];

  // Count surveys by type
  const getTypeCount = (type) => {
    return allSurveys.filter((survey) => survey.type === type).length;
  };

  // Filter surveys based on search and type
  const filteredSurveys = allSurveys.filter((survey) => {
    const matchesSearch =
      searchValue === "" ||
      survey.title.toLowerCase().includes(searchValue.toLowerCase()) ||
      survey.description.toLowerCase().includes(searchValue.toLowerCase());

    const matchesType = selectedType === "all" || survey.type === selectedType;

    return matchesSearch && matchesType;
  });

  const handleSurveySelect = (survey) => {
    setSelectedSurvey(survey);
    setIsDetailSheetOpen(true);
  };

  const handleCloseDetailSheet = () => {
    setIsDetailSheetOpen(false);
    setSelectedSurvey(null);
  };

  // Check if current user can edit the survey
  const canEditSurvey = (survey) => {
    // Only Pulse surveys (user-created) can be edited
    if (survey.type !== "Pulse" || !survey.originalTemplate) {
      return false;
    }

    const originalTemplate = survey.originalTemplate;
    
    // Check if user is the creator
    const isCreator = originalTemplate?.user_tasks?.find(
      (task) =>
        task?.task_type === "created_by" && task?.user_id === currentUserId
    );

    // Check if user is a collaborator
    const collaboratorsTask = originalTemplate?.user_tasks?.find(
      (task) => task?.task_type === "collaborators"
    );
    const isCollaborator =
      collaboratorsTask?.collaborators?.includes(currentUserId);

    return Boolean(isCreator || isCollaborator);
  };

  // Handle edit survey
  const handleEditSurvey = (survey) => {
    navigate(`/leader/audit/schedule-v2/pulse/edit/${survey.id}`, {
      state: { returnTo: location.pathname, from: 'library' },
    });
    handleCloseDetailSheet();
  };

  // QuickSend wizard handlers
  const handleUseTemplate = (survey) => {
    if (navigateToForm) {
      const surveyData = {
        id: survey.id,
        name: survey.title,
        description: survey.description,
        questions: survey.questions,
        feedbackTipsEnabled: true,
      };
      // Add is_draft user_task for surveys created from Pulse templates
      if (survey.type === "Pulse" || survey?.library) {
        surveyData.user_tasks = [
          {
            task_type: "is_draft",
            value: true,
          },
        ];
      }

      // Navigate to the new 2-step page instead of opening modal
      navigate("/leader/audit/schedule-v2/pulse/schedule", {
        state: {
          initialSurveyData: {
            ...surveyData,
            emailSubject: `Survey: ${survey.title || survey.name || ""}`,
            emailBody: `Hi,\n\nYou have been invited to participate in the survey "${
              survey.title || survey.name || ""
            }". Please follow the link to complete the survey.\n\nThank you!`,
            assignedUsersList: [],
          },
        },
      });
    }
    handleCloseDetailSheet();
  };

  // Note: QuickSend modal handlers removed - now using page-based flow

  const handleCreateSurvey = () => {
    navigate("/leader/audit/schedule-v2/pulse/create", {
      state: { returnTo: location.pathname, from: 'library' },
    });
  };


  const getSectionInfo = () => {
    if (selectedType === "all") {
      return {
        title: "All Surveys",
        description: "View all surveys across all categories",
      };
    }

    const typeDescriptions = {
      Retention:
        "Surveys focused on employee satisfaction, engagement, and retention",
      Culture:
        "Surveys measuring organizational culture, values, and communication",
      Leadership:
        "Surveys evaluating leadership effectiveness and organizational change",
      Onboarding:
        "Surveys designed to assess and improve the new employee experience",
      Pulse: "",
    };

    return {
      title: `${
        selectedType === "Pulse" ? "My Surveys" : selectedType + " Surveys"
      }`,
      description: typeDescriptions[selectedType],
    };
  };

  const sectionInfo = getSectionInfo();

  return (
    <>
      <PageContainer>
        <HeaderContainer>
          <TitleSection>
            {/* <BackLink onClick={handleBackToSurveys}>
              <ArrowLeftIcon />
              <span>Back to Surveys</span>
            </BackLink> */}
            <TitleGroup>
              <MainTitle>Survey Library</MainTitle>
              <Subtitle>
                Manage and organize your surveys across different collections
              </Subtitle>
            </TitleGroup>
          </TitleSection>
          <CreateButton onClick={handleCreateSurvey}>
            + Create Survey
          </CreateButton>
        </HeaderContainer>

        <ContentContainer>
          <Sidebar>
            <LibrarySection>
              <SectionTitle>Survey Types</SectionTitle>

              <LibraryItem
                active={selectedType === "all"}
                onClick={() => setSelectedType("all")}
              >
                <LibraryItemText active={selectedType === "all"}>
                  All Types
                </LibraryItemText>
                <CountBadge>{allSurveys.length}</CountBadge>
              </LibraryItem>

              {surveyTypes.map((type) => (
                <React.Fragment key={type}>
                  <LibraryItem
                    active={selectedType === type}
                    onClick={() => setSelectedType(type)}
                  >
                    <LibraryItemText active={selectedType === type}>
                      {type === "Pulse" ? "My Surveys" : type}
                    </LibraryItemText>
                    <CountBadge>{getTypeCount(type)}</CountBadge>
                  </LibraryItem>
                  {type === "Onboarding" && <SectionDivider />}
                </React.Fragment>
              ))}
            </LibrarySection>
          </Sidebar>

          <MainContent>
            <SearchContainer>
              <SearchInput
                type="text"
                placeholder="Search surveys..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
            </SearchContainer>

            <SurveyInfo>
              <SectionHeading>{sectionInfo.title}</SectionHeading>
              <SectionDescription>{sectionInfo.description}</SectionDescription>
              <SurveyCount>{filteredSurveys.length} surveys</SurveyCount>
            </SurveyInfo>

            <SurveysGrid>
              {filteredSurveys.map((survey) => {
                return (
                  <SurveyCard
                    key={survey.id}
                    survey={survey}
                    onSelect={() => handleSurveySelect(survey)}
                    highlighted={
                      highlightedSurvey &&
                      (survey.name === highlightedSurvey ||
                        survey.title === highlightedSurvey)
                    }
                  />
                );
              })}
            </SurveysGrid>
          </MainContent>
        </ContentContainer>
      </PageContainer>

      <SurveyDetailSheet
        isOpen={isDetailSheetOpen}
        onClose={handleCloseDetailSheet}
        survey={selectedSurvey}
        onUseTemplate={handleUseTemplate}
        onEdit={handleEditSurvey}
        canEdit={selectedSurvey ? canEditSurvey(selectedSurvey) : false}
      />

      {/* Note: QuickSend modal removed - now using page-based flow */}
    </>
  );
};

export default SurveyLibrary; 
